import { Message } from '../types';

interface LogEntry {
  timestamp: string;
  messageId: string;
  content: string;
  isError: boolean;
}

/**
 * Service for logging user-visible messages in memory
 * - Logs first line + timestamp for normal messages
 * - Logs full error message for errors
 * - Provides copy log functionality from in-memory messages
 */
export class MessageLogService {
  private static logEntries: LogEntry[] = [];

  /**
   * Log a message that the user sees
   * Note: This is kept for potential future use, but the copy log feature
   * works directly from the messages array passed to it.
   */
  static logMessage(message: Message): void {
    const isError = message.modelUsed === 'ionoff-error';
    
    // For errors, log full content
    // For normal messages, log first line only
    let contentToLog: string;
    if (isError) {
      contentToLog = message.content;
    } else {
      // Get first line (or first 100 chars if no newline)
      const firstLine = message.content.split('\n')[0];
      contentToLog = firstLine.length > 100 ? firstLine.substring(0, 100) + '...' : firstLine;
    }

    const timestamp = message.timestamp.toISOString();
    
    const logEntry: LogEntry = {
      timestamp,
      messageId: message.id,
      content: contentToLog,
      isError,
    };

    this.logEntries.push(logEntry);
  }

  /**
   * Get log entries between a user prompt and a target message
   * This builds the log directly from the messages array for real-time accuracy
   * Includes full content for debug info messages
   * Only includes expandable details (API calls) if they are currently expanded in the UI
   */
  static getLogBetweenPromptAndMessage(
    messages: Message[],
    targetMessageId: string,
    expandedMessageIds: Set<string> = new Set()
  ): string {
    // Find the target message
    const targetIndex = messages.findIndex(m => m.id === targetMessageId);
    if (targetIndex === -1) return '';

    // Find the previous user message (the prompt)
    let promptIndex = -1;
    for (let i = targetIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        promptIndex = i;
        break;
      }
    }

    if (promptIndex === -1) return '';

    // Get all messages between prompt and target (inclusive)
    const relevantMessages = messages.slice(promptIndex, targetIndex + 1);
    
    // Build log string
    const logLines: string[] = [];
    
    relevantMessages.forEach((message, idx) => {
      const isError = message.modelUsed === 'ionoff-error';
      const isDebugInfo = message.isDebugInfo;
      let contentToLog: string;
      
      // For errors and debug info: log full content
      if (isError || isDebugInfo) {
        contentToLog = message.content;
      } else {
        // For normal messages: log first line
        const firstLine = message.content.split('\n')[0];
        contentToLog = firstLine.length > 100 ? firstLine.substring(0, 100) + '...' : firstLine;
      }

      const timestamp = message.timestamp.toISOString();
      const prefix = message.role === 'user' 
        ? '[USER]' 
        : isError 
        ? '[ERROR]' 
        : '[INFO]';
      
      logLines.push(`${prefix} ${timestamp} | ${contentToLog}`);
      
      // Only include expanded details if this message's dropdown is actually expanded in the UI
      const isExpanded = expandedMessageIds.has(message.id);
      
      if (isExpanded && message.debug?.openrouterRequest) {
        logLines.push('  [API REQUEST]');
        logLines.push('  ' + JSON.stringify(message.debug.openrouterRequest, null, 2).split('\n').join('\n  '));
      }
      
      if (isExpanded && message.debug?.synthesisApiCall) {
        logLines.push('  [SYNTHESIS API CALL]');
        logLines.push('  ' + JSON.stringify(message.debug.synthesisApiCall, null, 2).split('\n').join('\n  '));
      }
      
      // Include grading API calls if present and expanded
      if (isExpanded && message.debug?.gradingApiCalls && message.debug.gradingApiCalls.length > 0) {
        logLines.push('  [GRADING API CALLS]');
        message.debug.gradingApiCalls.forEach((call, i) => {
          logLines.push(`  Call ${i + 1}:`);
          logLines.push('  ' + JSON.stringify(call, null, 2).split('\n').join('\n  '));
        });
      }
    });

    return logLines.join('\n');
  }

  /**
   * Download log file (creates a downloadable file from in-memory logs)
   */
  static downloadLog(): void {
    const content = this.logEntries.map(entry => {
      const prefix = entry.isError ? '[ERROR]' : '[INFO]';
      return `${prefix} ${entry.timestamp} | ${entry.content}\n`;
    }).join('');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ionoff-messages-${new Date().toISOString().split('T')[0]}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Clear all log entries
   */
  static clearLog(): void {
    this.logEntries = [];
  }

  /**
   * Get current log entries count
   */
  static getLogCount(): number {
    return this.logEntries.length;
  }
}
